import React, { useState } from 'react';
import { X, Sparkles, Baby, Heart, Crown, MessageCircle, User } from 'lucide-react';

/**
 * 🎁 Élodie #1095 — Modal de bienvenue "Pack Bienvenue MamaLink"
 * Affichée UNE seule fois à la première connexion après activation du pack.
 * 4 slides : Félicitations → Baby+Mom Planner → Premium → Prochaines étapes.
 */
const StarterPackWelcomeModal = ({ open, daysLeft, firstName, onClose, onGoToChat, onGoToProfile }) => {
  const [step, setStep] = useState(0);
  if (!open) return null;

  const slides = [
    {
      icon: <Sparkles className="w-10 h-10 text-[#E8A5C4]" strokeWidth={2.2} />,
      title: `Félicitations ${firstName || ''} 🎁`,
      body: (
        <div className="space-y-3">
          <p className="text-center text-sm text-[#9D8189] font-black uppercase tracking-widest">
            Tu viens de recevoir ton
          </p>
          <p className="text-center text-2xl font-black bg-gradient-to-r from-[#E8A5C4] to-[#D48CB0] bg-clip-text text-transparent leading-tight">
            Pack Bienvenue MamaLink
          </p>
          <p className="text-sm text-[#5C3D36] leading-relaxed text-center pt-2">
            Pendant <strong>7 jours</strong>, tu accèdes gratuitement à <strong>TOUT</strong> ce que
            MamaLink offre de plus doux 🌸 — c'est notre petit cadeau pour t'accueillir dans le
            cocon 💕
          </p>
        </div>
      ),
      color: 'from-[#FFF5F3] to-[#FCE4EC]',
    },
    {
      icon: (
        <div className="flex gap-2">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFD5C8] to-[#FFB5A7] flex items-center justify-center">
            <Baby className="w-7 h-7 text-white" strokeWidth={2.2} />
          </div>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E8A5C4] to-[#D48CB0] flex items-center justify-center">
            <Heart className="w-7 h-7 text-white" strokeWidth={2.2} />
          </div>
        </div>
      ),
      title: 'Baby Planner + Mom Planner 🌸',
      subtitle: 'Tes deux planners sont ouverts',
      body: (
        <ul className="text-sm text-[#5C3D36] space-y-2 leading-relaxed">
          <li>🍼 <strong>Baby Planner</strong> : suivi sommeil, tétées, courbes de poids, étapes, santé peau, DME…</li>
          <li>💕 <strong>Mom Planner</strong> : en essai, grossesse, post-partum — trouve ce qui est fait pour toi et le suivi de tes journées.</li>
        </ul>
      ),
      color: 'from-[#FFD5C8]/40 to-[#E8A5C4]/40',
    },
    {
      icon: (
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFD700] to-[#FFB84D] flex items-center justify-center">
          <Crown className="w-7 h-7 text-white" strokeWidth={2.2} />
        </div>
      ),
      title: 'Tous les super-pouvoirs Premium 👑',
      subtitle: 'Débloqués pour toi',
      body: (
        <ul className="text-sm text-[#5C3D36] space-y-1.5 leading-relaxed">
          <li>💕 Matching illimité avec d'autres mamans</li>
          <li>💬 Messages privés sans limite</li>
          <li>👀 Voir qui a visité ton profil</li>
          <li>✨ Boost de profil, photos illimitées</li>
          <li>🎁 Et plein d'autres petites attentions premium</li>
        </ul>
      ),
      color: 'from-[#FFF3D0]/40 to-[#FFD700]/30',
    },
    {
      icon: <Heart className="w-10 h-10 text-[#E8A5C4]" strokeWidth={2.2} />,
      title: 'Pour bien commencer 🌷',
      subtitle: 'Deux petits gestes pour te lancer',
      body: (
        <div className="space-y-3">
          <button
            onClick={onGoToProfile}
            className="w-full flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-[#FFF5F3] to-[#FCE4EC] hover:shadow-md transition-all text-left"
            data-testid="starter-pack-goto-profile"
          >
            <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-[#5C3D36]" strokeWidth={2.2} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-[#5C3D36]">Remplis ton profil 🌸</p>
              <p className="text-[11px] text-[#9D8189]">Photo, parcours, situation — pour que les autres mamans te trouvent</p>
            </div>
          </button>
          <button
            onClick={onGoToChat}
            className="w-full flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-[#F0E6FF] to-[#E0BBE4]/40 hover:shadow-md transition-all text-left"
            data-testid="starter-pack-goto-chat"
          >
            <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-5 h-5 text-[#5C3D36]" strokeWidth={2.2} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-[#5C3D36]">Présente-toi dans le Chat en ligne 💬</p>
              <p className="text-[11px] text-[#9D8189]">Un petit bonjour aux autres mamans — elles seront ravies de t'accueillir</p>
            </div>
          </button>
        </div>
      ),
      color: 'from-[#FFF5F3] to-[#F0E6FF]/60',
    },
  ];

  const s = slides[step];
  const isLast = step === slides.length - 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="starter-pack-welcome-modal">
      <div className={`relative w-full max-w-sm bg-gradient-to-br ${s.color} bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300`}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-sm"
          aria-label="Fermer"
          data-testid="starter-pack-close"
        >
          <X className="w-4 h-4 text-[#5C3D36]" strokeWidth={2.5} />
        </button>

        {/* Bandeau J-X */}
        {daysLeft > 0 && (
          <div className="bg-gradient-to-r from-[#E8A5C4] to-[#D48CB0] text-white text-center py-1.5 text-[11px] font-black uppercase tracking-widest">
            🎁 Ton pack expire dans {daysLeft} jour{daysLeft > 1 ? 's' : ''}
          </div>
        )}

        <div className="p-6 pt-8 space-y-4">
          <div className="flex justify-center">{s.icon}</div>
          <div className="text-center space-y-1">
            {s.subtitle && (
              <p className="text-[11px] uppercase tracking-widest text-[#9D8189] font-bold">{s.subtitle}</p>
            )}
            <h2 className="text-xl font-bold text-[#5C3D36] font-serif">{s.title}</h2>
          </div>

          <div className="min-h-[110px]">{s.body}</div>

          {/* Dots */}
          <div className="flex justify-center gap-1.5 pt-2">
            {slides.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-[#E8A5C4]' : 'w-1.5 bg-[#E8A5C4]/30'}`}
              />
            ))}
          </div>

          {/* Nav */}
          <div className="flex gap-2 pt-2">
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="flex-1 py-2.5 rounded-full bg-white/80 hover:bg-white text-[#5C3D36] text-sm font-bold shadow-sm"
                data-testid="starter-pack-prev"
              >
                Retour
              </button>
            )}
            <button
              onClick={() => (isLast ? onClose() : setStep(step + 1))}
              className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-[#E8A5C4] to-[#D48CB0] hover:shadow-lg text-white text-sm font-bold shadow-md transition-all"
              data-testid="starter-pack-next"
            >
              {isLast ? 'C\'est parti 🌸' : 'Suivant'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StarterPackWelcomeModal;
